import React from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import theme from '@src/styles/theme';
import {
  BUSINESS_PACKAGE_CODES,
  FREE_PACKAGE_CODES,
  PACKAGE_DURATION,
  PACKAGE_LEVEL,
  PACKAGE_STUDIO_VERSION,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { PAYMENT_HUB_RENEWAL_TYPE } from '@src/constants/subscription';
import {
  calculateRemainingResource,
  checkCustomPackage,
} from '@src/services/package';
import CreditIntro from '@src/containers/CreditIntro';
import useUserInfo from '@src/hooks/useTtsUser';
import { COLOR } from '@src/styles/color';
import route from '@src/constants/route';
import { CONTACT_US_PAGE } from '@src/configs';
import { MOBILE_BREAKPOINT } from '@src/constants';
import useWindowDimensions from '@src/hooks/useWindowDimensions';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import usePackageData from './hooks/usePackageData';
import PackageCard from './components/PackageCard';
import ApiPackageConversionNotification from './components/ApiPackageConversionNotification';
import { StyledSubscriptionNew } from './index.style';

const SubscriptionNew = ({
  usingOrders = [],
  subscriptionPurchase,
  totalRemainingCredits = 0,
  totalRemainingApiCredits = 0,
  totalCapturedRolloverCredits = 0,
  capturedReferralCreditsCycle = 0,
  remainingCustomCredits = 0,
  capturedReferralCreditsCustom = 0,
  purchasedTopUpCredits = 0,
  //   lastUsingOrder = {},
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('smd'));
  const { width } = useWindowDimensions();
  const { user: userInfo } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();
  const { studio } = ttsUser || {};
  const { cycle } = studio || {};
  const { creditsPerCycle, nextResetAt } = cycle || {};
  const { getAutoRenewalPlan } = useMappingPackage();

  const { determinePackageCase } = usePackageData();
  const packageCase = determinePackageCase(usingOrders, userInfo);
  const { handleGetTopUpPackageCode } = useMappingPackage();

  const autoRenewalPlan = getAutoRenewalPlan(subscriptionPurchase);

  // Get current package (V3/Custom) or first package for button logic
  const getCurrentPackageForButton = () => {
    const currentOrder = usingOrders.find(
      (order) =>
        (order?.package?.version?.includes(PACKAGE_STUDIO_VERSION.V3) ||
          order?.package?.level === PACKAGE_LEVEL.CUSTOM) &&
        !FREE_PACKAGE_CODES.includes(order?.package?.code) &&
        order?.package?.type === PACKAGE_TYPE.STUDIO,
    );
    return currentOrder || usingOrders[0] || {};
  };

  const currentPackageForButton = getCurrentPackageForButton();
  const { code, type, level, version } = currentPackageForButton?.package || {};
  const isBusinessPackage = BUSINESS_PACKAGE_CODES.includes(code);
  const isFreePackage = FREE_PACKAGE_CODES.includes(code);
  const isCreditIntroMobile = width <= MOBILE_BREAKPOINT;
  const isCustomPackage = checkCustomPackage({ type, level });
  const isV2Package =
    !version?.includes(PACKAGE_STUDIO_VERSION.V3) &&
    type === PACKAGE_TYPE.STUDIO &&
    !FREE_PACKAGE_CODES.includes(code);
  const currentPackageExpiryDate = currentPackageForButton?.packageExpiryDate;
  const hasOneTimeExpired =
    currentPackageExpiryDate &&
    moment(currentPackageExpiryDate).isBefore(moment());
  const isApiPackage = usingOrders?.some(
    (order) => order?.package?.type === PACKAGE_TYPE.API,
  );
  const handleRedirectToBuyPackage = () => {
    const redirectUrl = `${route.PAYMENT}`;
    if (type === PACKAGE_TYPE.API) {
      window.open(redirectUrl);
    } else {
      history.push(redirectUrl);
    }
  };

  const handleClickBuyMoreChar = () => {
    const topUpPackageCode = handleGetTopUpPackageCode({
      type,
      level,
    });
    const redirectUrl = `${route.PAYMENT}?package=${topUpPackageCode}`;
    history.push(redirectUrl);
  };

  const handleClickContactSupport = () =>
    window.open(CONTACT_US_PAGE, '_blank', 'noopener,noreferrer');

  const renderHeaderButton = () => {
    if (isBusinessPackage) {
      return (
        <Button
          className="buy-more-btn"
          onClick={handleClickContactSupport}
          sx={{
            backgroundColor: COLOR.yellow.default,
            color: COLOR.indigo.default,
            fontSize: '14px',
            fontWeight: 600,
            padding: '8px 16px',
            borderRadius: '8px',
            textTransform: 'none',
            lineHeight: '20px',
            letterSpacing: '-0.4px',
            '&:hover': {
              backgroundColor: COLOR.primary,
              opacity: 0.9,
            },
          }}
        >
          {t('contact')}
        </Button>
      );
    }

    if (isFreePackage || isV2Package || hasOneTimeExpired || isCustomPackage) {
      return (
        <Button
          className="buy-more-btn"
          onClick={handleRedirectToBuyPackage}
          sx={{
            backgroundColor: COLOR.yellow.default,
            color: COLOR.indigo.default,
            fontSize: '14px',
            fontWeight: 600,
            padding: '8px 16px',
            borderRadius: '8px',
            textTransform: 'none',
            lineHeight: '20px',
            letterSpacing: '-0.4px',
            '&:hover': {
              backgroundColor: COLOR.primary,
              opacity: 0.9,
            },
          }}
        >
          {t('buyNow')}
        </Button>
      );
    }

    return (
      <Button
        className="buy-more-btn"
        onClick={handleClickBuyMoreChar}
        sx={{
          backgroundColor: COLOR.yellow.default,
          color: COLOR.indigo.default,
          fontSize: '14px',
          fontWeight: 600,
          padding: '8px 16px',
          borderRadius: '8px',
          textTransform: 'none',
          lineHeight: '20px',
          letterSpacing: '-0.4px',
          '&:hover': {
            backgroundColor: COLOR.primary,
            opacity: 0.9,
          },
        }}
      >
        {t('buyMoreCharacters')}
      </Button>
    );
  };

  const getV2PackageData = () => {
    const v2Order = usingOrders.find(
      (order) =>
        !order?.package?.version?.includes(PACKAGE_STUDIO_VERSION.V3) &&
        order?.package?.type === PACKAGE_TYPE.STUDIO,
    );

    if (!v2Order && userInfo?.remainingCharacters) {
      // Fallback to userInfo if no V2 order found
      return {
        packageInfo: { code: userInfo?.studio?.oneTime?.packageCode },
        usingOrder: {
          packageExpiryDate:
            userInfo?.studio?.oneTime?.packageExpiryDate ||
            userInfo?.packageExpiryDate,
        },
        remainingCredits:
          userInfo.remainingCharacters + userInfo.bonusCharacters || 0,
      };
    }

    const remainingResource = calculateRemainingResource(
      PACKAGE_TYPE.STUDIO,
      userInfo,
      false,
    );

    return {
      packageInfo: v2Order?.package || {},
      usingOrder: v2Order || {},
      remainingCredits: remainingResource || 0,
    };
  };

  const getCurrentPackageData = (order) => {
    const isV3 =
      order?.package?.version?.includes(PACKAGE_STUDIO_VERSION.V3) &&
      !FREE_PACKAGE_CODES.includes(order?.package?.code);
    const isCustom = order?.package?.level === PACKAGE_LEVEL.CUSTOM;
    const isFree = FREE_PACKAGE_CODES.includes(order?.package?.code);
    const packageExpiryDate = order?.packageExpiryDate;
    const isExpired =
      packageExpiryDate && moment(packageExpiryDate).isBefore(moment());
    const isMonthlyPackage = order?.package?.code?.includes(
      PACKAGE_DURATION.MONTH,
    );
    let remainingCredits = 0;
    let totalCredits = 0;
    let apiLimitRemaining = 0;
    let apiLimitTotal = 0;
    let showRolloverInfo = false;
    let rolloverCredits = 0;
    let rolloverDate = null;
    let showNextResetCycle = false;
    let professionalVoices;
    let instantVoices;
    let concurrentDevices;
    let concurrentRequest;
    let sttDurationSeconds;

    if (isCustom) {
      remainingCredits = remainingCustomCredits;
      // Custom packages may have API access
      apiLimitRemaining = remainingCustomCredits; // Simplified - should come from API
    } else if (isV3) {
      totalCredits =
        creditsPerCycle +
        purchasedTopUpCredits +
        totalCapturedRolloverCredits +
        capturedReferralCreditsCycle;
      remainingCredits = totalRemainingCredits;
      apiLimitTotal = Math.round(totalCredits * 0.5); // Simplified - should come from API
      apiLimitRemaining = Math.min(
        apiLimitTotal - totalRemainingApiCredits,
        totalRemainingCredits,
      ); // Simplified

      const hasNextResetCycle = moment(nextResetAt).isBefore(
        packageExpiryDate,
        'day',
      );
      showNextResetCycle = !isMonthlyPackage && hasNextResetCycle;
    } else if (isFree) {
      remainingCredits = userInfo?.bonusCharacters || 0;
      totalCredits = creditsPerCycle || 3000;
      showRolloverInfo = true;
      rolloverCredits = 3000; // Default free package reset amount
      rolloverDate = nextResetAt;
      showNextResetCycle = true;
    } else {
      remainingCredits =
        userInfo?.remainingCharacters + userInfo?.bonusCharacters || 0;
    }

    // const hasAutoRenew = checkAutoRenewOrder(order?.renewalType);
    const isAutoRenewalPackage =
      order?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;

    if (!isV2Package) {
      professionalVoices = ttsUser?.maxCloningVoices;
      instantVoices = ttsUser?.maxInstantVoiceCloning;
      concurrentDevices = ttsUser?.maxDevices || ttsUser?.device;
      concurrentRequest = ttsUser?.concurrentRequestApi;
      sttDurationSeconds = ttsUser?.stt?.remainingSeconds;
    }

    // Ensure all values are safe to render

    return {
      packageInfo: order?.package || {},
      usingOrder: order || {},
      isExpired,
      isV3Package: isV3,
      isCustomPackage: isCustom,
      isFreePackage: isFree,
      remainingCredits,
      totalCredits,
      apiLimitRemaining,
      apiLimitTotal,
      showRolloverInfo,
      rolloverCredits,
      rolloverDate,
      hasAutoRenew: isAutoRenewalPackage,
      professionalVoices,
      instantVoices,
      concurrentDevices,
      concurrentRequest,
      sttDurationSeconds,
      paymentMethodType: order?.paymentMethod?.type,
      showNextResetCycle,
      nextResetAt,
      creditsPerCycle,
      purchasedTopUpCredits,
      totalCapturedRolloverCredits,
      capturedReferralCreditsCycle,
      capturedReferralCreditsCustom,
      autoRenewalPlan,
      isBusinessPackage,
      orderInfo: order || {},
    };
  };

  // isCreditIntroMobile is compare to MOBILE_BREAKPOINT (768px) which is the mobile breakpoint of header.
  // isMobile using smd of MUI is not compatible since on this breakpoint, the header doesn't change its layout
  const renderCreditIntroButton = () => (
    <CreditIntro isMobile={isCreditIntroMobile} />
  );

  const renderPackageCards = () => {
    if (packageCase.showTwoColumnLayout) {
      // Two column: Current (V3/Custom) + Old (V2)
      const currentOrder = usingOrders.find(
        (order) =>
          (order?.package?.version?.includes(PACKAGE_STUDIO_VERSION.V3) ||
            order?.package?.level === PACKAGE_LEVEL.CUSTOM) &&
          !FREE_PACKAGE_CODES.includes(order?.package?.code),
      );
      const v2Data = getV2PackageData();
      const currentData = getCurrentPackageData(currentOrder);

      return (
        <>
          <PackageCard
            {...currentData}
            statusType="current"
            isMobile={isMobile}
            subscriptionPurchase={subscriptionPurchase}
            isTwoPackage
          />
          <PackageCard
            packageInfo={v2Data.packageInfo}
            usingOrder={v2Data.usingOrder}
            statusType="old"
            remainingCredits={v2Data.remainingCredits}
            showV2Warning
            isMobile={isMobile}
            isExpired={
              v2Data.usingOrder?.packageExpiryDate &&
              moment(v2Data.usingOrder.packageExpiryDate).isBefore(moment())
            }
          />
        </>
      );
    }

    // Single column: Only one package
    const currentOrder = usingOrders[0] || {};
    const currentData = getCurrentPackageData(currentOrder);
    const isV2Only =
      !currentData.isV3Package &&
      !currentData.isCustomPackage &&
      !currentData.isFreePackage;

    return (
      <PackageCard
        {...currentData}
        statusType="current"
        showV2Warning={isV2Only}
        isMobile={isMobile}
        subscriptionPurchase={subscriptionPurchase}
      />
    );
  };

  if (!usingOrders?.length) return null;

  return (
    <StyledSubscriptionNew>
      <Box className="container">
        <Box className="header-container">
          <Typography className="title">{t('myPackages')}</Typography>
          {isMobile && isApiPackage && <ApiPackageConversionNotification />}
          <div className="btn-action">
            {renderCreditIntroButton()}
            {renderHeaderButton()}
          </div>
        </Box>
        {!isMobile && isApiPackage && (
          <Box className="notification-container">
            <ApiPackageConversionNotification />
          </Box>
        )}
        {usingOrders?.length ? (
          <Box className="packages-container">{renderPackageCards()}</Box>
        ) : null}
      </Box>
    </StyledSubscriptionNew>
  );
};

export default SubscriptionNew;
